import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { CardPicker } from "@/components/CardPicker";
import { CardImage } from "@/components/CardImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Info, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONDS = ["Mint", "Near Mint", "Excellent", "Light Played", "Played", "Poor"];
const LANGS = ["IT", "EN", "JP", "FR", "DE", "ES"];
const COND_GUIDE = "Mint: perfetta · Near Mint: quasi perfetta · Excellent: lievi segni · Light Played: usura evidente · Played: molto usata · Poor: danneggiata. Le condizioni sono dichiarazioni dell'utente, non certificazioni di Omniverse.";

const empty = { card: null, quantity: 1, language: "IT", condition: "Near Mint", notes: "", available_for_trade: true, public: true, graded: false, company: "", grade: "", photos: [] };

function fromAvailability(a) {
  return {
    card: a.card, quantity: a.quantity, language: a.language, condition: a.condition, notes: a.notes || "",
    available_for_trade: a.available_for_trade, public: a.public, graded: a.grading?.graded || false,
    company: a.grading?.company || "", grade: a.grading?.grade || "", photos: a.photos || [],
  };
}

// Shared create/edit dialog for availabilities. `editing` = availability object or null.
export function AvailabilityFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? fromAvailability(editing) : empty);
  }, [open, editing]);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      } catch (e) { toast.error(apiError(e)); }
    }
    setUploading(false);
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const save = async () => {
    if (!form.card) { toast.error("Seleziona una carta."); return; }
    if (!form.photos.length) { toast.error("Carica almeno una foto reale della carta."); return; }
    setSaving(true);
    const payload = { card_id: form.card.id, quantity: Number(form.quantity), language: form.language, condition: form.condition,
      grading: { graded: form.graded, company: form.company || null, grade: form.grade || null }, notes: form.notes,
      available_for_trade: form.available_for_trade, public: form.public, photos: form.photos };
    try {
      if (editing) { await api.put(`/availabilities/${editing.id}`, payload); toast.success("Annuncio aggiornato."); }
      else { await api.post("/availabilities", payload); toast.success("Annuncio pubblicato."); }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#141B2D] border-[#2A3655] text-[#F8FAFC] sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica annuncio" : "Aggiungi una carta"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!editing && <CardPicker onSelect={(c) => setForm((f) => ({ ...f, card: c }))} selectedId={form.card?.id} />}
          {form.card && (
            <>
              <div className="rounded-lg border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 p-2 text-sm text-[#F8FAFC]">Carta: <b>{form.card.name}</b> · {form.card.expansion_name || "—"}</div>

              <div>
                <Label className="text-xs text-[#CBD5E1]">Foto della carta (obbligatorie) *</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {form.photos.map((p) => (
                    <div key={p} className="relative w-16" data-testid="photo-thumb">
                      <CardImage path={p} card={form.card} className="w-16" />
                      <button type="button" onClick={() => removePhoto(p)} data-testid="remove-photo" aria-label="Rimuovi foto" className="absolute -right-1.5 -top-1.5 rounded-full bg-[#EF4444] p-0.5 text-white"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <label className="flex w-16 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#2A3655] text-[#94A3B8] hover:border-[#2DD4BF] hover:text-[#2DD4BF]" style={{ aspectRatio: "3/4" }}>
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden data-testid="photo-input" onChange={(e) => onFiles(e.target.files)} />
                  </label>
                </div>
                <p className="mt-1 text-xs text-[#64748B]">JPEG, PNG o WebP · max 5 MB per file. Carica foto reali della tua carta (puoi aggiungerne più di una, es. fronte e retro).</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs text-[#CBD5E1]">Quantità</Label><Input type="number" min="1" data-testid="card-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
                <div><Label className="text-xs text-[#CBD5E1]">Lingua</Label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}><SelectTrigger data-testid="card-language" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs text-[#CBD5E1]">Condizione</Label>
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}><SelectTrigger data-testid="card-condition" className="bg-[#0B1020] border-[#2A3655]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#141B2D] border-[#2A3655] text-[#F8FAFC]">{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <p className="flex items-start gap-1 text-xs text-[#64748B]"><Info className="mt-0.5 h-3 w-3 shrink-0" />{COND_GUIDE}</p>
              <label className="flex items-center gap-2 text-sm text-[#CBD5E1]"><Switch checked={form.graded} onCheckedChange={(v) => setForm({ ...form, graded: v })} data-testid="card-graded" />Carta gradata</label>
              {form.graded && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs text-[#CBD5E1]">Società (dichiarata)</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="PSA, BGS…" className="bg-[#0B1020] border-[#2A3655]" /></div>
                  <div><Label className="text-xs text-[#CBD5E1]">Voto (dichiarato)</Label><Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="10, 9.5…" className="bg-[#0B1020] border-[#2A3655]" /></div>
                </div>
              )}
              <div><Label className="text-xs text-[#CBD5E1]">Note</Label><Textarea data-testid="card-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" /></div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center justify-between text-sm text-[#CBD5E1]">Disponibile allo scambio<Switch checked={form.available_for_trade} onCheckedChange={(v) => setForm({ ...form, available_for_trade: v })} data-testid="card-available" /></label>
                <label className="flex items-center justify-between text-sm text-[#CBD5E1]">Visibile pubblicamente<Switch checked={form.public} onCheckedChange={(v) => setForm({ ...form, public: v })} data-testid="card-public" /></label>
              </div>
            </>
          )}
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || uploading || !form.card || !form.photos.length} data-testid="save-card" className="bg-[#7C3AED] hover:bg-[#6D28D9]">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
